/**
 * Email notification service for Transfer Outcomes workflow
 * Uses unified notification endpoint
 */

import { jwtDecode } from 'jwt-decode';
import { getLastKnownAccessToken } from '../../auth/authTokenProvider';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { post } from '../apiService';

interface ExtendedTokenPayload {
  exp?: number;
  customer_id: string;
  role?: string;
  permission_group?: string;
  sub?: string; // Auth0 user ID
  email?: string;
  name?: string;
}

interface NotificationParams {
  alertId?: string;
  entityType: 'practice' | 'provider';
  entityId: string;
  link?: string;
  entityName: string;
}

interface UserInfo {
  email: string;
  id: string;
  name: string;
  permissionGroup: string;
}

/**
 * Get user information from JWT token
 * Exported for use in components to create comment log entries
 */
export function getUserInfoFromToken(): UserInfo | null {
  const token = getLastKnownAccessToken();
  if (!token) {
    console.warn('Access token is missing. Cannot get user info.');
    return null;
  }

  try {
    const decoded = jwtDecode<ExtendedTokenPayload>(token);
    const userId =
      decoded.sub || decoded.customer_id || 'unknown-user';
    const email = decoded.email || '';
    const name = decoded.name || '';
    const permissionGroup = decoded.permission_group || '';

    return {
      email,
      id: userId,
      name,
      permissionGroup,
    };
  } catch (error) {
    console.error('Failed to decode token for user info:', error);
    return null;
  }
}

/**
 * Notify Denim users when a new alert/note is created or updated
 * Called when "Save Alert" button is clicked
 */
export async function notifyDenimOnSave(
  params: NotificationParams
): Promise<void> {
  try {
    const customerId = getCustomerIdFromToken();
    if (!customerId) {
      console.warn(
        'Customer ID is missing. Cannot send notification.'
      );
      return;
    }

    const url = `resourceMatrix/send/notifications?customerId=${customerId}`;
    const body = {
      alertId: params.alertId,
      entityType: params.entityType,
      entityId: params.entityId,
      link:
        params.link ||
        generateAlertLink(
          params.entityType,
          params.entityId,
          params.alertId
        ),
      triggerType: 'save_alert',
      entityName: params.entityName,
      approvedBy: null,
    };

    await post(url, body);
  } catch (error) {
    // Log error but don't block the save operation
    console.error(
      'Failed to send notification to Denim on save:',
      error
    );
    // Don't throw - notification failure shouldn't prevent saving
  }
}

/**
 * Notify CHS users when a note's classification is submitted for approval
 * Called when "Submit for Approval" button is clicked
 */
export async function notifyCHSOnSubmission(
  params: NotificationParams
): Promise<void> {
  try {
    const customerId = getCustomerIdFromToken();
    if (!customerId) {
      console.warn(
        'Customer ID is missing. Cannot send notification.'
      );
      return;
    }

    const url = `resourceMatrix/send/notifications?customerId=${customerId}`;
    const body = {
      alertId: params.alertId,
      entityType: params.entityType,
      entityId: params.entityId,
      link:
        params.link ||
        generateAlertLink(
          params.entityType,
          params.entityId,
          params.alertId
        ),
      triggerType: 'approve_transfer',
      entityName: params.entityName,
      approvedBy: null,
    };

    await post(url, body);
  } catch (error) {
    // Log error but don't block the submission
    console.error(
      'Failed to send notification to CHS on submission:',
      error
    );
    // Don't throw - notification failure shouldn't prevent submission
  }
}

/**
 * Notify Denim users when CHS approves the transfer outcomes
 * Called when "Approve Transfer Outcomes" button is clicked
 */
export async function notifyDenimOnApproval(
  params: NotificationParams
): Promise<void> {
  try {
    const customerId = getCustomerIdFromToken();
    if (!customerId) {
      console.warn(
        'Customer ID is missing. Cannot send notification.'
      );
      return;
    }

    const url = `resourceMatrix/send/notifications?customerId=${customerId}`;
    const userInfo = getUserInfoFromToken();

    const body = {
      alertId: params.alertId,
      entityType: params.entityType,
      entityId: params.entityId,
      link:
        params.link ||
        generateAlertLink(
          params.entityType,
          params.entityId,
          params.alertId
        ),
      triggerType: 'approved',
      entityName: params.entityName,
      // Format approvedBy as string: "name (email) userId" for better readability
      // Backend API expects string, not object
      approvedBy: userInfo
        ? [
            userInfo.name || '',
            userInfo.email ? `(${userInfo.email})` : '',
            userInfo.id || '',
          ]
            .filter(Boolean)
            .join(' ')
        : '',
    };

    await post(url, body);
  } catch (error) {
    // Log error but don't block the approval
    console.error(
      'Failed to send notification to Denim on approval:',
      error
    );
    // Don't throw - notification failure shouldn't prevent approval
  }
}

/**
 * Generate a link to the specific alert/note
 * Includes query params for deep linking support
 * Backend expects alertId for both alerts and notes
 */
export function generateAlertLink(
  entityType: 'practice' | 'provider',
  entityId: string,
  alertId?: string
): string {
  const baseUrl = window.location.origin;
  const basePath =
    entityType === 'practice'
      ? `${baseUrl}/practices/${entityId}`
      : `${baseUrl}/providers/${entityId}`;

  // If we have alertId, add query param for deep linking
  // Backend expects alertId for both alerts and notes
  if (alertId) {
    return `${basePath}?alertId=${alertId}`;
  }

  // Fallback to entity link without query params
  return basePath;
}

